import { Injectable } from '@angular/core';
import { PaginationClientService } from "../../../../services/client/base/pagination-client.service";
import { CompoApp } from "../../../../services/CompoApp";
import { FILTER_OPTIONS_HEADER_COLUMNS } from "./filter-options-header-columns";
import { DynamicFilterRequestDto } from "../../../../models/dynamic-filter/dynamic-filter-request-dto.model";
import { IPageInfo } from '@iharbeck/ngx-virtual-scroller';
import { FilterOption, FilterOptionPriority } from "../../../../models/filter-option/filter-option.model";
import { debounceTime, delay, filter, skip, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { BehaviorSubject, firstValueFrom, NEVER, Observable, of, ReplaySubject, Subject } from "rxjs";
import { FilterPageNameEnum } from "../../../../enums/dynamic-filter/FilterPageNameEnum";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { DynamicFilterEntryDto } from "../../../../models/dynamic-filter/dynamic-filter-entry-dto.model";
import { FilterUiTypeEnum } from "../../../../enums/dynamic-filter/FilterUiTypeEnum";
import { FilterUiRenderOptionEnum } from "../../../../enums/dynamic-filter/FilterUiRenderOptionEnum";
import { InputTagType, ValueType } from "../../../components/input/form-input/form-input.enums";
import { FilterUiRenderDirectionEnum } from "../../../../enums/dynamic-filter/FilterUiRenderDirectionEnum";
import Constants from "../../../../Constants";
import {
  FilterOptionInputType,
  FilterOptionType,
  filterOptionTypeToFilterPage
} from "../../../../enums/filter-options/filter-option.enum";
import { OperatingScopeEnum } from "../../../../enums/operating-scope.enum";
import { AuthserviceService } from '../../auth-page/login-page/authservice.service';
import { DynamicFilterTemplateService } from '../../../../services/dynamic-filter-template.service';
import { DynamicFilterTemplatePage } from '../../../../models/dynamic-filter/dynamic-filter-template-page.model';

@Injectable()
export class FilterOptionsSubPageClientService extends PaginationClientService<FilterOption> {
  private readonly filterTemplate$_ = new BehaviorSubject<DynamicFilterTemplatePage | null>(null);
  private readonly filterEntriesByName$_ = new ReplaySubject<Map<string, [DynamicFilterEntryDto]>>(1);
  private readonly activeSubMenu$_ = new BehaviorSubject<FilterOptionType>(FilterOptionType.OBJECT);
  private readonly confirmCreateVisible$_ = new Subject<boolean>();
  private readonly confirmDeleteVisible$_ = new Subject<boolean>();
  private readonly actionInProgress$_ = new Subject<boolean>();
  private readonly selectAllInProgress$_ = new Subject<boolean>();
  private readonly listActionsEnabled$_ = new ReplaySubject<boolean>(1);


  readonly selectAllControlName = 'selectAll';
  readonly selectAllControl = new UntypedFormControl();
  readonly selectionForm = new UntypedFormGroup({
    [this.selectAllControlName]: this.selectAllControl
  });
  private fetchedFilterTemplates = new Map<FilterPageNameEnum, DynamicFilterTemplatePage>();
  private activeFilterOptionIds = new Set<string>();
  private filterOptionToCreate: FilterOption = null;
  private filterOptionToDelete: FilterOption = null;
  private nameEntryId: string;
  private relevanceEntryId: string;
  private relevanceEntryOptionsLength: number;
  private belongsToCommunityEntryId: string;
  private belongsToCommunity: boolean = false;

  get nameSearchEntryId(): string {
    return this.nameEntryId;
  }

  get communityScope(): boolean {
    return this.belongsToCommunity;
  }

  get filterEntriesByName$(): Observable<Map<string, [DynamicFilterEntryDto]>> {
    return this.filterEntriesByName$_.asObservable();
  }

  get activeSubMenu$(): Observable<FilterOptionType> {
    return this.activeSubMenu$_.asObservable();
  }

  get confirmCreateVisible$(): Observable<boolean> {
    return this.confirmCreateVisible$_.asObservable();
  }

  get confirmDeleteVisible$(): Observable<boolean> {
    return this.confirmDeleteVisible$_.asObservable();
  }

  get actionInProgress$(): Observable<boolean> {
    return this.actionInProgress$_.asObservable();
  }

  get selectAllInProgress$(): Observable<boolean> {
    return this.selectAllInProgress$_.asObservable();
  }

  get listActionsEnabled$(): Observable<boolean> {
    return this.listActionsEnabled$_.asObservable();
  }

  constructor(
    public app: CompoApp,
    private auth: AuthserviceService,
    public dynamicFilterTemplateService: DynamicFilterTemplateService
  ) {
    super(app, dynamicFilterTemplateService);
    this.pageSize$.next(10);
    this.sortDtoColumns$.next(FILTER_OPTIONS_HEADER_COLUMNS);
    this.activeSubMenu$_.next(FilterOptionType.OBJECT);
    this.init();
  }

  private init(): void {
    if (this.auth.isAuthenticated() && this.auth.noRequestInProgressActive()) {
      this.initSubMenuChanges();
      this.initAllSelection();
      this.initFilterFormChanges();
    }
  }

  //region INIT
  private initSubMenuChanges(): void {
    this.activeSubMenu$_
      .pipe(
        takeUntil(this.unSubscribe$),
        filter(() => !!this.authenticated$),
        switchMap(activeSubMenu => {
          this.selectAllControl.disable({emitEvent: false});
          this.values$.next(Array.from({length: this.pageSize$.getValue()}));
          const filterPage = filterOptionTypeToFilterPage(activeSubMenu);
          this.filterTemplate$_.next(null);
          this.filterEntriesByName$_.next(null);
          if (this.fetchedFilterTemplates.has(filterPage)) {
            return of(this.fetchedFilterTemplates.get(filterPage));
          } else {
            return this.dynamicFilterTemplateService.getByPageEnum(filterPage)
              .then(filterTemplate => {
                this.fetchedFilterTemplates.set(filterPage, filterTemplate);
                return filterTemplate;
              });
          }
        }),
        delay(500),
        switchMap(filterTemplate => {
          const controlNames = Object.keys(this.filterForm.controls);
          controlNames.forEach(controlName => this.filterForm.removeControl(controlName));
          const filterEntriesByName = new Map<string, [DynamicFilterEntryDto]>();


          Object.entries(filterTemplate.filterEntries).forEach(([_, entry]) => {
            filterEntriesByName.set(entry.filterName, [entry]);
            this.filterForm.addControl(entry.id, new UntypedFormControl());
          });


          const entryName = filterTemplate.filterEntries.NAME;

          entryName.uiType = FilterUiTypeEnum.TEXT;
          entryName.uiRenderOption = FilterUiRenderOptionEnum.TEXT_INLINE;
          entryName.valueType = ValueType.TEXT;
          entryName.inputTagType = InputTagType.SEARCH_INPUT;
          this.nameEntryId = entryName.id;

          const entryRelevanceName = filterTemplate.filterEntries.RELEVANCE;

          entryRelevanceName.uiType = FilterUiTypeEnum.MULTIPLE_CHECKBOX;
          entryRelevanceName.valueType = ValueType.TEXT;
          entryRelevanceName.inputTagType = InputTagType.SEARCH_INPUT;
          entryRelevanceName.uiRenderOption = FilterUiRenderOptionEnum.LIST;
          entryRelevanceName.uiRenderDirection = FilterUiRenderDirectionEnum.HORIZONTAL;
          switch (filterTemplate.page) {
            case FilterPageNameEnum.FILTER_OPTIONS_OBJECT:
              entryRelevanceName.enumValues = Constants.DROPDOWNS.filterOptionRelevantEntries.object;
              break;
            case FilterPageNameEnum.FILTER_OPTIONS_JOB:
              entryRelevanceName.enumValues = Constants.DROPDOWNS.filterOptionRelevantEntries.job;
              break;
            case FilterPageNameEnum.FILTER_OPTIONS_MESSAGE:
              entryRelevanceName.enumValues = Constants.DROPDOWNS.filterOptionRelevantEntries.message;
              break;
          }
          this.filterForm.controls[entryRelevanceName.id].setValue(entryRelevanceName.enumValues.map(enumValue => enumValue.value));
          this.relevanceEntryId = entryRelevanceName.id;
          this.relevanceEntryOptionsLength = entryRelevanceName.enumValues.length;

          const entryBelongsToCommunity = filterTemplate.filterEntries.BELONGS_TO_COMMUNITY;
          this.filterForm.controls[entryBelongsToCommunity.id].setValue(false);
          entryBelongsToCommunity.enumValues = [
            {
              label: this.app.i18n.data.DESIGN_B.ADMIN_PAGE.FILTER_OPTIONS.BELONGS_TO.ORGANIZATION,
              icon: 'building',
              value: false
            },
            {
              label: this.app.i18n.data.DESIGN_B.ADMIN_PAGE.FILTER_OPTIONS.BELONGS_TO.COMMUNITY,
              icon: 'users-class',
              value: true
            }
          ];
          this.belongsToCommunityEntryId = entryBelongsToCommunity.id;

          const entryShowOnlySelected = filterTemplate.filterEntries.SHOW_ONLY_SELECTED;
          this.filterForm.controls[entryShowOnlySelected.id].setValue(false);
          entryShowOnlySelected.enumValues = [
            {label: 'DESIGN_B.ADMIN_PAGE.FILTER_OPTIONS.DISPLAY_OPTIONS.ALL_OPTIONS', icon: null, value: false},
            {
              label: 'DESIGN_B.ADMIN_PAGE.FILTER_OPTIONS.DISPLAY_OPTIONS.SELECTED_ONLY',
              icon: 'check-circle',
              value: true
            }
          ];

          this.filterTemplate$_.next(filterTemplate);
          this.filterEntriesByName$_.next(filterEntriesByName);
          return this.app.filterOptionService.getAllActive(filterTemplate.page);
        }),
        switchMap(activeFilterOptions => {
          this.activeFilterOptionIds.clear();
          activeFilterOptions.forEach(filterOption => this.activeFilterOptionIds.add(filterOption.id));
          this.resetValues();
          return this.totalCount$.pipe(skip(1), take(1));
        })
      )
      .subscribe((totalCount) => {
        this.selectAllControl.enable({emitEvent: false});
        if (totalCount > this.activeFilterOptionIds.size) {
          this.selectAllControl.setValue(false, {emitEvent: false});
        } else {
          this.selectAllControl.setValue(true, {emitEvent: false});
        }
      });
  }

  resetFilter() {
    super.resetFilter();
    this.setFilterDefaultValue();
  }


  private setFilterDefaultValue() {
    this.filterEntriesByName$.forEach(val => {
      for (const [key, value] of val) {
        switch (key) {
          case 'name': {
            this.filterForm.controls[value[0].id.toString()].setValue(null);
            break;
          }
          case 'belongsToCommunity': {
            this.filterForm.controls[value[0].id.toString()].setValue(false);
            break;
          }
          case 'showOnlySelected': {
            this.filterForm.controls[value[0].id.toString()].setValue(false);
            break;
          }
        }
      }
    });
  }

  private initAllSelection(): void {
    this.selectAllControl.valueChanges
      .pipe(
        takeUntil(this.unSubscribe$),
        filter(() => !!this.authenticated$))
      .subscribe(allSelected => {
        this.selectAllInProgress$_.next(true);
        if (allSelected === true) {
          this.app.filterOptionService.selectAll(this.createFilterDto())
            .then((selectedFilterOptionIds) => {
              selectedFilterOptionIds.forEach(id => this.activeFilterOptionIds.add(id));
              this.resetValues();
            })
            .catch(() => this.selectAllControl.setValue(false, {emitEvent: false}))
            .finally(() => this.selectAllInProgress$_.next(false));
        } else if (allSelected === false) {
          this.selectAllInProgress$_.next(true);
          this.app.filterOptionService.deselectAll(this.createFilterDto())
            .then(() => {
              this.activeFilterOptionIds.clear();
              this.resetValues();
            })
            .catch(() => this.selectAllControl.setValue(true, {emitEvent: false}))
            .finally(() => this.selectAllInProgress$_.next(false));
        }
      });
  }

  private initFilterFormChanges(): void {
    this.filterTemplate$_
      .pipe(
        takeUntil(this.unSubscribe$),
        switchMap(filterTemplate => {
          if (!filterTemplate) {
            return NEVER;
          } else {
            return this.filterForm.valueChanges;
          }
        })
      ).subscribe(filterValues => {
      if (filterValues[this.nameEntryId]
        && filterValues[this.nameEntryId] !== null && filterValues[this.nameEntryId] !== '') {
        this.listActionsEnabled$_.next(false);
        this.selectAllControl.disable({emitEvent: false});
        return;
      }
      if (filterValues[this.relevanceEntryId]
        && filterValues[this.relevanceEntryId].length !== this.relevanceEntryOptionsLength
        && filterValues[this.relevanceEntryId].length !== 0) {
        this.listActionsEnabled$_.next(false);
        this.selectAllControl.disable({emitEvent: false});
        return;
      }
      if (filterValues.hasOwnProperty(this.belongsToCommunityEntryId)
        && this.belongsToCommunity != filterValues[this.belongsToCommunityEntryId]) {
        this.belongsToCommunity = !this.belongsToCommunity;
        const dto = this.createFilterDto();
        setTimeout(() => {
          if (this.belongsToCommunity) {
            this.selectAllControl.disable({emitEvent: false});
            this.listActionsEnabled$_.next(false);
          } else {
            this.selectAllControl.enable({emitEvent: false});
            this.listActionsEnabled$_.next(true);
          }
          // Selection disabled for community
          // this.selectAllControl.disable({emitEvent: false});
          this.app.filterOptionService.getAllActive(this.filterTemplate$_.getValue().page, dto)
            .then(activeFilterOptions => {
              this.activeFilterOptionIds.clear();
              activeFilterOptions.forEach(filterOption => this.activeFilterOptionIds.add(filterOption.id));
              return firstValueFrom(this.totalCount$.pipe(take(1)));
            })
            .then((totalCount) => {
              // Selection disabled for community
              // this.selectAllControl.enable({emitEvent: false});
              // if (totalCount > this.activeFilterOptionIds.size) {
              //   this.selectAllControl.setValue(false, {emitEvent: false});
              // } else {
              //   this.selectAllControl.setValue(true, {emitEvent: false});
              // }
            });
        });
      }
      this.listActionsEnabled$_.next(true);
      this.selectAllControl.enable({emitEvent: false});
    });


    this.filterForm.valueChanges
      .pipe(takeUntil(this.unSubscribe$), debounceTime(500))
      .subscribe(() => this.resetValues());
  }

  // endregion


  //region CREATE
  public create(filterOption: FilterOption): void {
    this.filterOptionToCreate = filterOption;
    this.confirmCreateVisible$_.next(true);
  }

  public confirmCreate(belongsToCommunity: [DynamicFilterEntryDto] | undefined): void {
    this.actionInProgress$_.next(true);
    this.app.filterOptionService.createOrUpdate(
      this.filterOptionToCreate,
      this.filterForm.controls[this.belongsToCommunityEntryId.toString()].value
        ? OperatingScopeEnum.COMMUNITY
        : OperatingScopeEnum.ORGANIZATION
    )
      .then((filterOption) => {
        this.activeFilterOptionIds.add(filterOption.id);
        if (!this.selectAllControl.value && this.totalCount$.getValue() <= this.activeFilterOptionIds.size) {
          this.selectAllControl.setValue(true, {emitEvent: false});
        }
        this.filterOptionToCreate = null;
        this.resetValues();
        this.confirmCreateVisible$_.next(false);
      })
      .finally(() => this.actionInProgress$_.next(false));
  }

  public cancelCreate(): void {
    this.confirmCreateVisible$_.next(false);
    this.filterOptionToCreate = null;
  }

  //endregion

  //region DELETE
  public delete(filterOption: FilterOption): void {
    this.filterOptionToDelete = filterOption;
    this.confirmDeleteVisible$_.next(true);
  }

  public confirmDelete(): void {
    this.actionInProgress$_.next(true);
    this.app.filterOptionService.delete(this.filterOptionToDelete)
      .then(() => {
        this.activeFilterOptionIds.delete(this.filterOptionToDelete.id);
        if (this.selectAllControl.value) {
          this.selectAllControl.setValue(false, {emitEvent: false});
        }
        this.filterOptionToDelete = null;
        this.resetValues();
        this.confirmDeleteVisible$_.next(false);
      })
      .finally(() => this.actionInProgress$_.next(false));
  }

  public cancelDelete(): void {
    this.confirmDeleteVisible$_.next(false);
    this.filterOptionToDelete = null;
  }

  //endregion

  //region SET_RANGE
  public setRangeInput(filterOption: FilterOption, rangeInput: boolean): Promise<FilterOption> {
    const updatedFilterOption = {
      ...filterOption,
      selectedCondition: rangeInput ? FilterOptionInputType.RANGE : FilterOptionInputType.EXACT_MATCH
    };
    return this.app.filterOptionService.createOrUpdate(
      updatedFilterOption,
      this.filterForm.controls[this.belongsToCommunityEntryId.toString()]?.value
        ? OperatingScopeEnum.COMMUNITY
        : OperatingScopeEnum.ORGANIZATION);
  }

  //endregion

  //region SET_PRIORITY
  public setOrder(filterOptions: FilterOption[]): Promise<void> {
    const priority: FilterOptionPriority[] = filterOptions
      .filter(filterOption => filterOption?.active)
      .map((filterOption, index) =>
        ({id: filterOption.id, order: index, isAttribute: !!filterOption.moduleAttrId}));
    return this.app.filterOptionService.setOrder(priority);
  }

  //endregion

  //region MISC
  public changeSubMenu(type: FilterOptionType): void {
    this.activeSubMenu$_.next(type);
    this.filterForm.controls[this.belongsToCommunityEntryId]?.setValue(false);
    this.belongsToCommunity = false;
  }

  //endregion

  //region PAGINATION
  createFilterDto(): DynamicFilterRequestDto {
    const filterTemplate = this.filterTemplate$_.getValue();
    if (!filterTemplate) return null;
    const dto = new DynamicFilterRequestDto();
    dto.templateId = filterTemplate?.id;
    dto.orgIds = [this.app.data.getUser().organization?.id];
    dto.filterValues = DynamicFilterRequestDto
      .initFilterValues(filterTemplate?.filterEntries, this.filterForm.value);
    return dto;
  }

  onVsChange(event: IPageInfo): void {
    if (this.filterTemplate$_.getValue() === null) {
      this.filterTemplate$_
        .pipe(
          filter(template => template != null),
          take(1),
          tap(() => this.onVsChange(event))
        ).subscribe();
    } else {
      const startPage = this.getPageNumber(event.startIndex);
      const endPage = this.getPageNumber(event.endIndex);

      const dto = this.createFilterDto();

      for (let i = startPage; i <= endPage; i++) {
        this.fetchPage(i, dto);
      }
    }
  }

  onVsEnd(event: IPageInfo): void {
  }

  onVsStart(event: IPageInfo): void {
  }

  //endregion
}
